import "@/utils/cspNonce"; // This runs CSP nonce handling before anything else
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
	createBrowserRouter,
	RouterProvider,
	// The below are required by Sentry's reactRouterV7BrowserTracingIntegration
	useLocation,
	useNavigationType,
	createRoutesFromChildren,
	matchRoutes,
} from "react-router";
// Sentry is now lazy-loaded, so we remove this static import
// import * as Sentry from "@sentry/react";
import { EEA } from "@/utils/EEA-codes";

// App layout and page/route components
import App from "@/App"; // This is our Root Layout
import NotFoundRedirect from "@/components/NotFoundRedirect/NotFoundRedirect";
import Home from "@/pages/Home/Home";
import Build from "@/pages/Build/Build";
import Privacy from "@/pages/Privacy/Privacy";
import Terms from "@/pages/Terms/Terms";
// TODO: comment out later, and the route at the bottom too
import PreviewToasts from "@/pages/PreviewToasts/PreviewToasts";
import ExportHighlightsPreview from "@/pages/Home/Summary/ExportHighlightsPreview";

// styles
import "@/css/defaults.css";
import "@/css/fonts.css";
import "@/css/colors.css";
import "@/css/base.css";
// don't change import order for toast styles
import "react-toastify/dist/ReactToastify.css";
import "@/css/toast.css";

// -----------------------------------------------------------------------------------

(async () => {
	// Initialize with default (non-Sentry) handlers and router creator.
	// These will be overridden if Sentry is enabled.
	let routerCreator = createBrowserRouter;
	let uncaughtErrorHandler = (error, errorInfo) => {
		console.warn("Uncaught error", error, errorInfo.componentStack);
	};
	let caughtErrorHandler;
	let recoverableErrorHandler;

	try {
		// Geo-location is dependent on Cloudflare's /cdn-cgi/trace endpoint.
		//
		// Fallback logic:
		// 1. If the request fails (network error or non-2xx response), Sentry is *not* initialised.
		// 2. If the response does not include a loc=XX line, Sentry is *not* initialised.
		// 3. Only when we can confidently determine that the user is *outside* the EEA do we
		//    enable Sentry. This conservative default protects user privacy and keeps us on
		//    the safe side of GDPR.
		let enableSentry = false;
		let country;
		// Helper: valid ISO alpha-2 code and not the "unknown" placeholder.
		function isRecognizedCountry(countryCode) {
			return /^[A-Z]{2}$/.test(countryCode) && countryCode !== "XX";
		}

		// 1. Try the Cloudflare-injected value first.
		const cfCountry = window.__CF_COUNTRY;
		if (typeof cfCountry === "string" && isRecognizedCountry(cfCountry)) {
			country = cfCountry;
		} else {
			// 2. Fallback to /cdn-cgi/trace when running locally or outside Cloudflare.
			try {
				const res = await fetch("/cdn-cgi/trace", { cache: "force-cache" });
				if (res.ok) {
					const text = await res.text();
					const locMatch = text.match(/loc=([A-Z]{2})/);
					const fetchedCode = locMatch ? locMatch[1] : undefined;
					if (fetchedCode && isRecognizedCountry(fetchedCode)) {
						country = fetchedCode;
					}
				} else {
					console.warn(
						`/cdn-cgi/trace responded with status ${res.status}; proceeding without location.`,
					);
				}
			} catch (err) {
				console.warn(
					"Failed to fetch geo-location; proceeding without location.",
					err,
				);
			}
		}

		// Enable Sentry only for visitors *outside* the EEA.
		if (country && !EEA.includes(country)) {
			enableSentry = true;
		}

		// Respect browser privacy signals: Do-Not-Track (both "1" and "yes") and Global Privacy Control.
		// Commented out bcs then i lose all error visibility for everyone using an adblocker
		// const dntEnabled =
		// 	navigator?.doNotTrack === "1" ||
		// 	window?.doNotTrack === "1" ||
		// 	navigator?.doNotTrack === "yes";
		// const gpcEnabled = navigator?.globalPrivacyControl === true;

		// if (dntEnabled || gpcEnabled) {
		// 	enableSentry = false;
		// 	console.warn(
		// 		"A privacy signal (Do Not Track / Global Privacy Control) is enabled; Sentry will remain disabled for this session.",
		// 	);
		// }

		if (enableSentry) {
			// Lazy-import keeps the Sentry bundle out of the initial chunk
			const Sentry = await import("@sentry/react");

			// -- SENTRY --
			const integrations = [
				Sentry.reactRouterV7BrowserTracingIntegration({
					useEffect,
					useLocation,
					useNavigationType,
					createRoutesFromChildren,
					matchRoutes,
				}),
			];

			if (import.meta.env.PROD) {
				integrations.push(
					Sentry.replayIntegration(),
					Sentry.captureConsoleIntegration({ levels: ["error"] }),
				);
			}

			Sentry.init({
				dsn: "https://bf8f14b056d4885e048340ef8f4d9a35@o4509010562908160.ingest.us.sentry.io/4509010567692288",
				environment: import.meta.env.MODE,
				integrations,
				tracesSampleRate: 0.2,
				tracePropagationTargets: [/^https:\/\/brainem\.co\/api/],
				replaysSessionSampleRate: 0.0,
				replaysOnErrorSampleRate: 1.0,
			});

			routerCreator = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
			uncaughtErrorHandler = Sentry.reactErrorHandler((error, errorInfo) => {
				console.warn("Uncaught error", error, errorInfo.componentStack);
			});
			caughtErrorHandler = Sentry.reactErrorHandler();
			recoverableErrorHandler = Sentry.reactErrorHandler();
		}
	} catch (error) {
		// This catch block now only handles errors during Sentry initialization.
		// The app will render without Sentry, but it won't crash.
		console.warn("Sentry initialization failed:", error);
	}

	// -- ROUTES --
	const router = routerCreator([
		{
			element: <App />,
			children: [
				{ path: "/", element: <Home key="home" isLinkMode={false} /> },
				{
					path: "/share/:id",
					element: <Home key="share" isLinkMode={true} />,
				},
				{ path: "/build", element: <Build key="build" /> },
				{ path: "/privacy", element: <Privacy key="privacy" /> },
				{ path: "/terms", element: <Terms key="terms" /> },
				{
					path: "/preview-toasts",
					element: <PreviewToasts key="preview-toasts" />,
				},
				{
					path: "/preview",
					element: <ExportHighlightsPreview onComplete={() => {}} />,
				},
				{ path: "*", element: <NotFoundRedirect /> },
			],
		},
	]);

	createRoot(document.getElementById("root"), {
		onUncaughtError: uncaughtErrorHandler,
		onCaughtError: caughtErrorHandler,
		onRecoverableError: recoverableErrorHandler,
	}).render(
		<StrictMode>
			<RouterProvider router={router} />
		</StrictMode>,
	);
})();
